<template>
  <div :class="wrapClasses">
    <label v-if="label" class="k-slider__label">
      {{ label }}
    </label>

    <div class="k-slider__input-container">
      <input
        type="range"
        class="k-slider__range"
        :min="min"
        :max="max"
        :step="step"
        :value="lazyValue"
        :disabled="disabled || readonly"
        :style="trackStyle"
        @input="onInput"
        @change="onChange"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'KSlider',
  
  props: {
    value: {
      type: [Number, String],
      default: 0
    },
    min: {
      type: [Number, String],
      default: 0
    },
    max: {
      type: [Number, String],
      default: 100
    },
    step: {
      type: [Number, String],
      default: 1
    },
    label: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: '#1976d2' // Vuetify primary default
    }
  },

  data() {
    return {
      lazyValue: this.value
    };
  },

  watch: {
    value(val) {
      this.lazyValue = val;
    }
  },

  computed: {
    wrapClasses() {
      return {
        'k-slider': true,
        'k-slider--disabled': this.disabled,
        'k-slider--readonly': this.readonly
      };
    },
    percentage() {
      const min = parseFloat(this.min);
      const max = parseFloat(this.max);
      const val = parseFloat(this.lazyValue);
      if (max === min || isNaN(val)) return 0;
      return ((val - min) / (max - min)) * 100;
    },
    // Luodaan dynaaminen taustaväri suoraan input-elementille
    trackStyle() {
      const fillColor = this.disabled ? '#bdbdbd' : this.color;
      const emptyColor = '#e0e0e0';
      
      return {
        background: `linear-gradient(to right, ${fillColor} 0%, ${fillColor} ${this.percentage}%, ${emptyColor} ${this.percentage}%, ${emptyColor} 100%)`,
        '--thumb-color': fillColor // Välitetään väri pallolle CSS-muuttujana
      };
    }
  },

  methods: {
    onInput(e) {
      const value = parseFloat(e.target.value);
      this.lazyValue = value; // Päivittää computed-arvon välittömästi Vue 2:ssa
      this.$emit('input', value);
    },
    onChange(e) {
      const value = parseFloat(e.target.value);
      this.$emit('change', value);
    }
  }
};
</script>

<style scoped>
.k-slider {
  display: flex;
  flex-direction: column;
  font-family: Roboto, sans-serif;
  margin-bottom: 16px;
  width: 100%;
}

.k-slider__label {
  font-size: 16px;
  color: rgba(0, 0, 0, 0.54);
  margin-bottom: 8px;
}

.k-slider__input-container {
  display: flex;
  align-items: center;
  height: 32px;
}

.k-slider__range {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  outline: none;
  margin: 0;
  cursor: pointer;
}

/* Pallon tyylittely Google Chrome, Safari, uusi Edge */
.k-slider__range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--thumb-color, #1976d2);
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.k-slider__range::-webkit-slider-thumb:hover {
  transform: scale(1.5);
}

/* Pallon tyylittely Firefox */
.k-slider__range::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border: none;
  border-radius: 50%;
  background: var(--thumb-color, #1976d2);
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.k-slider__range::-moz-range-thumb:hover {
  transform: scale(1.5);
}

/* Tilat */
.k-slider--disabled .k-slider__range {
  cursor: not-allowed;
}

.k-slider--readonly .k-slider__range {
  pointer-events: none;
}
</style>